// Vendor
import { format } from 'date-fns';
import styled from 'styled-components';

// Components
import Tag from 'src/components/atoms/Tag';

// Helpers
import { getResultStatusColorTag, transformResultToReadable } from 'src/features/fdeInbox/helpers';

// Types
import { TableProps } from 'src/components/molecules/Table';
import theme from 'src/theme';
import { EntryLogWorkflow, EntryResultType, IEntryLog } from 'src/ts/interfaces';

type TableEntryLog = {
  folderName?: string;
  workflowType?: string;
  hasResultTag?: boolean;
  rowColor?: string;
  result?: EntryResultType;
  submissionTime?: string;
  rulingTime?: string;
};

const columnNames = {
  folderName: 'Folder Name',
  workflowType: 'Review Type',
  result: 'Result',
  rulingTime: 'Ruling Time',
  submissionTime: 'Submission Time'
} as const;

// Format submission entries timestamps to readable format
const formatEntriesTimestamps = (entryLogs: TableEntryLog[]): TableEntryLog[] => {
  return entryLogs.map((entryLog) => {
    const { submissionTime, rulingTime } = entryLog;
    const formattedSubmissionTime = submissionTime
      ? format(new Date(submissionTime), 'MM/dd/yyyy HH:mm')
      : 'No Date';
    const formattedRulingTime = rulingTime
      ? format(new Date(rulingTime), 'MM/dd/yyyy HH:mm')
      : 'No Date';

    return {
      ...entryLog,
      submissionTime: formattedSubmissionTime,
      rulingTime: formattedRulingTime
    };
  });
};

const StyledTag = styled(Tag)`
  height: 32px;
`;

// Keyed on the EntryLogWorkflow union so adding a workflow without a config
// entry fails to compile. Only fraud reviews carry an entry_result; the rest
// write entry_result: nil.
const WORKFLOW_CONFIGS: Record<
  EntryLogWorkflow,
  { label: string; hasResultTag: boolean; rowColor: string }
> = {
  fraud_detection: {
    label: 'Fraud Detection',
    hasResultTag: true,
    rowColor: theme.color.purple[100]
  },
  income_verification: {
    label: 'Income Verification',
    hasResultTag: false,
    rowColor: theme.color.blue[100]
  },
  // Specialty keeps the fraud color it has always rendered with; only VOA gets
  // its own color here (ITL-2996 AC).
  specialty_income: {
    label: 'Specialty Income',
    hasResultTag: false,
    rowColor: theme.color.purple[100]
  },
  asset_verification: {
    label: 'Asset Verification',
    hasResultTag: false,
    rowColor: theme.color.green[100]
  }
};

// Missing/unrecognized workflows render as fraud: rows predating the
// workflow field are fraud reviews.
const getWorkflowConfig = (workflow?: EntryLogWorkflow) =>
  (workflow && WORKFLOW_CONFIGS[workflow]) || WORKFLOW_CONFIGS.fraud_detection;

// Add the Tags components to the submission entries data
const addResultTags = (entryLogs: TableEntryLog[]) => {
  return entryLogs.map((entryLog) => {
    if (!entryLog.hasResultTag) {
      return entryLog;
    }
    const ResultTag = (
      <StyledTag
        label={transformResultToReadable(entryLog?.result)}
        color={getResultStatusColorTag(entryLog?.result)}
        showBackground
      />
    );
    return {
      ...entryLog,
      result: ResultTag
    };
  });
};

// Format columns data to be displayed in table
export const getColumnsData = (): TableProps['columns'] => {
  const columns = Object.keys(columnNames).map((key) => {
    const label = columnNames[key as keyof typeof columnNames] || key;
    if (key === 'result') return { label, field: key, width: 240 };
    return { label, field: key };
  });
  return columns;
};

// Given a list of entry logs, return the data required for the table
export const getTableData = (entryLogs: IEntryLog[]): TableProps => {
  const tableData: TableEntryLog[] = entryLogs.map((log) => {
    const workflow = getWorkflowConfig(log?.workflow);

    return {
      folderName: log?.entry?.folder?.name,
      workflowType: workflow.label,
      hasResultTag: workflow.hasResultTag,
      rowColor: workflow.rowColor,
      result: log?.entry_result as EntryResultType,
      submissionTime: log.entry?.submission_time ?? log.entry?.inserted_at,
      rulingTime: log.entry?.folder?.ruling_time ?? log?.inserted_at
    };
  });

  const columns = getColumnsData();
  const dataWithFormattedDates = formatEntriesTimestamps(tableData);
  const dataWithResultTags = addResultTags(dataWithFormattedDates);
  return { columns, data: dataWithResultTags };
};
